import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Upload, Play, Clock, Trophy, AlertCircle, CheckCircle, X, ArrowRight, ArrowLeft } from 'lucide-react';
import { useCredits } from '../contexts/CreditContext';
import { getApiUrl } from '../config';
import confetti from 'canvas-confetti';

function GameMaker() {
  const { updateCreditsFromResponse } = useCredits();
  const [files, setFiles] = useState([]);
  const [gameTitle, setGameTitle] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Game state
  const [gameData, setGameData] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [timeLeft, setTimeLeft] = useState(60);
  const [isGameActive, setIsGameActive] = useState(false);
  const [score, setScore] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [gameComplete, setGameComplete] = useState(false);
  const [timerInterval, setTimerInterval] = useState(null);
  const [hasTriggeredConfetti, setHasTriggeredConfetti] = useState(false);

  const handleFileChange = (e) => {
    setFiles(Array.from(e.target.files));
    setError('');
    setSuccess('');
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!files.length) {
      setError('Please upload at least one file.');
      return;
    }
    if (!gameTitle.trim()) {
      setError('Please enter a game title.');
      return;
    }
    
    setIsGenerating(true);
    setError('');
    setSuccess('');
    
    try {
      const formData = new FormData();
      files.forEach((file) => formData.append('files', file));
      formData.append('game_title', gameTitle);
      
      const response = await axios.post(getApiUrl('/generate_game'), formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        withCredentials: true,
      });
      
      setGameData(response.data.questions);
      setSuccess('Game generated successfully! Click "Start Game" to begin.');
      updateCreditsFromResponse(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate game. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const startGame = () => {
    setIsGameActive(true);
    setCurrentQuestionIndex(0);
    setScore(0);
    setGameComplete(false);
    setShowAnswer(false);
    setSelectedAnswer(null);
    setTimeLeft(60);
    startTimer();
  };

  const startTimer = () => {
    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          handleTimeUp();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    setTimerInterval(interval);
  };

  const handleTimeUp = () => {
    setShowAnswer(true);
    setTimeout(() => {
      nextQuestion();
    }, 3000);
  };

  const handleAnswerSelect = (answerIndex) => {
    if (selectedAnswer !== null) return; // Prevent multiple selections
    
    setSelectedAnswer(answerIndex);
    clearInterval(timerInterval);
    
    const currentQuestion = gameData[currentQuestionIndex];
    const isCorrect = answerIndex === currentQuestion.correct_answer;
    
    if (isCorrect) {
      setScore(prev => prev + 10);
      
      // Small confetti burst for correct answers
      confetti({
        particleCount: 30,
        spread: 70,
        origin: { y: 0.6 }
      });
    }
    
    setShowAnswer(true);
    
    setTimeout(() => {
      nextQuestion();
    }, 3000);
  };

  const nextQuestion = () => {
    if (currentQuestionIndex < gameData.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      setSelectedAnswer(null);
      setShowAnswer(false);
      setTimeLeft(60);
      startTimer();
    } else {
      endGame();
    }
  };

  const endGame = () => {
    setIsGameActive(false);
    setGameComplete(true);
    clearInterval(timerInterval);
    
    // Trigger confetti celebration
    if (!hasTriggeredConfetti) {
      setHasTriggeredConfetti(true);
      const isPerfectScore = score === gameData.length * 10;
      triggerConfetti(isPerfectScore);
    }
  };

  const triggerConfetti = (isPerfectScore = false) => {
    // Create a fun confetti celebration
    const duration = isPerfectScore ? 5000 : 3000; // Longer celebration for perfect scores
    const animationEnd = Date.now() + duration;
    const defaults = { 
      startVelocity: isPerfectScore ? 40 : 30, 
      spread: 360, 
      ticks: 60, 
      zIndex: 0 
    };

    function randomInRange(min, max) {
      return Math.random() * (max - min) + min;
    }

    const interval = setInterval(function() {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = (isPerfectScore ? 80 : 50) * (timeLeft / duration);
      
      // Create confetti from multiple angles
      confetti(Object.assign({}, defaults, {
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
      }));
      confetti(Object.assign({}, defaults, {
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
      }));
      
      // Extra celebration for perfect scores
      if (isPerfectScore && Math.random() > 0.7) {
        confetti({
          particleCount: 20,
          angle: 60,
          spread: 55,
          origin: { x: 0, y: 0.8 }
        });
        confetti({
          particleCount: 20,
          angle: 120,
          spread: 55,
          origin: { x: 1, y: 0.8 }
        });
      }
    }, 250);
  };

  const resetGame = () => {
    setGameData(null);
    setCurrentQuestionIndex(0);
    setSelectedAnswer(null);
    setTimeLeft(60);
    setIsGameActive(false);
    setScore(0);
    setShowAnswer(false);
    setGameComplete(false);
    setHasTriggeredConfetti(false);
    clearInterval(timerInterval);
  };

  const getCurrentQuestion = () => {
    if (!gameData || currentQuestionIndex >= gameData.length) return null;
    return gameData[currentQuestionIndex];
  };

  const getAnswerClass = (answerIndex) => {
    const currentQuestion = getCurrentQuestion();
    if (!currentQuestion) return '';
    
    if (!showAnswer) {
      return selectedAnswer === answerIndex ? 'bg-blue-500 text-white' : 'bg-white hover:bg-gray-50';
    }
    
    if (answerIndex === currentQuestion.correct_answer) {
      return 'bg-green-500 text-white';
    }
    
    if (selectedAnswer === answerIndex && answerIndex !== currentQuestion.correct_answer) {
      return 'bg-red-500 text-white';
    }
    
    return 'bg-gray-200 text-gray-600';
  };

  if (isGameActive && gameData) {
    const currentQuestion = getCurrentQuestion();
    
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white rounded-lg shadow-lg p-8">
          {/* Game Header */}
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-800 mb-2">{gameTitle}</h2>
            <div className="flex justify-center items-center space-x-8">
              <div className="flex items-center space-x-2">
                <Clock className="w-5 h-5 text-blue-600" />
                <span className="text-2xl font-bold text-blue-600">{timeLeft}s</span>
              </div>
              <div className="flex items-center space-x-2">
                <Trophy className="w-5 h-5 text-yellow-600" />
                <span className="text-2xl font-bold text-yellow-600">{score} pts</span>
              </div>
              <div className="text-gray-600">
                Question {currentQuestionIndex + 1} of {gameData.length}
              </div>
            </div>
          </div>

          {/* Question Card */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-8 mb-8">
            <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">
              {currentQuestion?.question}
            </h3>
            
            {/* Answer Options */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {currentQuestion?.options?.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleAnswerSelect(index)}
                  disabled={selectedAnswer !== null}
                  className={`p-4 rounded-lg border-2 border-gray-200 text-left font-medium transition-all duration-200 ${
                    selectedAnswer !== null ? 'cursor-default' : 'cursor-pointer hover:border-blue-300'
                  } ${getAnswerClass(index)}`}
                >
                  <div className="flex items-center justify-between">
                    <span>{option}</span>
                    {showAnswer && index === currentQuestion.correct_answer && (
                      <CheckCircle className="w-6 h-6" />
                    )}
                    {showAnswer && selectedAnswer === index && index !== currentQuestion.correct_answer && (
                      <X className="w-6 h-6" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Answer Feedback */}
          {showAnswer && (
            <div className="text-center mb-6">
              {selectedAnswer === currentQuestion?.correct_answer ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center justify-center space-x-2">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                    <span className="text-green-800 font-semibold">Correct! +10 points</span>
                  </div>
                </div>
              ) : (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center justify-center space-x-2">
                    <X className="w-6 h-6 text-red-600" />
                    <span className="text-red-800 font-semibold">
                      Incorrect. The correct answer was: {currentQuestion?.options[currentQuestion.correct_answer]}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (gameComplete) {
    const accuracy = Math.round((score / (gameData.length * 10)) * 100);
    const isPerfectScore = score === gameData.length * 10;
    const isHighScore = accuracy >= 80;
    
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="text-center">
            <div className="mb-8">
              <Trophy className={`w-16 h-16 mx-auto mb-4 ${
                isPerfectScore ? 'text-yellow-500 animate-bounce' : 
                isHighScore ? 'text-yellow-600' : 'text-yellow-600'
              }`} />
              <h2 className="text-3xl font-bold text-gray-800 mb-4">
                {isPerfectScore ? '🎉 Perfect Score! 🎉' : 
                 isHighScore ? '🌟 Excellent Work! 🌟' : 'Game Complete!'}
              </h2>
              <p className="text-xl text-gray-600 mb-6">Final Score: {score} points</p>
              
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 mb-8">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Performance Summary</h3>
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <div className={`text-2xl font-bold ${
                      isPerfectScore ? 'text-yellow-600' : 'text-blue-600'
                    }`}>{score}</div>
                    <div className="text-sm text-gray-600">Total Points</div>
                  </div>
                  <div>
                    <div className={`text-2xl font-bold ${
                      isPerfectScore ? 'text-yellow-600' : 'text-green-600'
                    }`}>{accuracy}%</div>
                    <div className="text-sm text-gray-600">Accuracy</div>
                  </div>
                </div>
                
                {/* Performance message */}
                <div className="mt-4">
                  {isPerfectScore && (
                    <div className="text-yellow-700 font-semibold text-lg">
                      🏆 Outstanding! You got every question right!
                    </div>
                  )}
                  {!isPerfectScore && isHighScore && (
                    <div className="text-green-700 font-semibold text-lg">
                      🌟 Great job! You have excellent knowledge!
                    </div>
                  )}
                  {!isPerfectScore && !isHighScore && (
                    <div className="text-blue-700 font-semibold text-lg">
                      👍 Good effort! Keep learning and improving!
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={startGame}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                Play Again
              </button>
              <button
                onClick={resetGame}
                className="px-6 py-3 bg-gray-600 text-white rounded-lg font-semibold hover:bg-gray-700 transition-colors"
              >
                Create New Game
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-8">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">
            Interactive Game Maker
          </h2>
          <p className="text-gray-600 text-lg">
            Upload your source materials and generate an interactive quiz game with countdown timers and scoring.
          </p>
        </div>

        {gameData && !isGameActive && (
          <div className="mb-8 p-6 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-green-800 mb-2">Game Ready!</h3>
                <p className="text-green-700">
                  {gameData.length} questions generated from your materials. Click "Start Game" to begin.
                </p>
              </div>
              <button
                onClick={startGame}
                className="px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors flex items-center space-x-2"
              >
                <Play className="w-5 h-5" />
                <span>Start Game</span>
              </button>
            </div>
          </div>
        )}

        <form onSubmit={handleGenerate} className="space-y-6">
          {/* Game Information */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Game Title *
            </label>
            <input
              type="text"
              value={gameTitle}
              onChange={(e) => setGameTitle(e.target.value)}
              placeholder="Enter game title..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload Source Materials *
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <input
                type="file"
                multiple
                onChange={handleFileChange}
                accept=".pdf,.docx,.pptx,.txt,.html,.htm"
                className="hidden"
                id="game-file-upload"
              />
              <label
                htmlFor="game-file-upload"
                className="cursor-pointer inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Upload className="w-4 h-4 mr-2" />
                Choose Files
              </label>
              <p className="mt-2 text-sm text-gray-500">
                Supported formats: PDF, DOCX, PPTX, TXT, HTML
              </p>
              {files.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-700">Selected files:</p>
                  <ul className="mt-2 text-sm text-gray-600">
                    {files.map((file, index) => (
                      <li key={index} className="flex items-center justify-between">
                        <span>{file.name}</span>
                        <span className="text-xs text-gray-500">
                          ({(file.size / 1024 / 1024).toFixed(2)} MB)
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* Generate Button */}
          <div className="flex justify-center">
            <button
              type="submit"
              disabled={isGenerating || files.length === 0 || !gameTitle.trim()}
              className={`px-8 py-3 text-lg font-medium rounded-md text-white ${
                isGenerating || files.length === 0 || !gameTitle.trim()
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500'
              }`}
            >
              {isGenerating ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                  Generating Game...
                </div>
              ) : (
                <div className="flex items-center">
                  <Play className="w-5 h-5 mr-2" />
                  Generate Game
                </div>
              )}
            </button>
          </div>
        </form>

        {/* Error Message */}
        {error && (
          <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <div className="flex">
              <div className="flex-shrink-0">
                <AlertCircle className="h-5 w-5 text-red-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-md">
            <div className="flex">
              <div className="flex-shrink-0">
                <CheckCircle className="h-5 w-5 text-green-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-green-700">{success}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default GameMaker; 